// Fonctions de passage entre un montant (nombre) et un objet Price
// (`amountExclTax` / `amountTax` / `amountInclTax` / `currency`), la forme
// utilisée par le sous-schéma Mongoose Price et le type GraphQL du même nom.

const DEFAULT_CURRENCY = "EUR";

// Un montant issu d'un calcul flottant ne peut pas être stocké tel quel :
// 490 × 1.2 vaut 587.9999999999999 en JavaScript. Tous les montants calculés
// ici sont ramenés au centime, l'unité dans laquelle les prix sont facturés.
export function roundToCents(amount) {
  return Math.round(amount * 100) / 100;
}

// Un montant à 0 est une valeur à part entière : seules l'absence de valeur
// et une valeur illisible donnent null.
function toAmount(value) {
  if (value === null || value === undefined || value === "") return null;
  const amount = Number(value);
  return Number.isNaN(amount) ? null : amount;
}

/**
 * Construit un Price à partir des deux montants déjà connus (cas d'une saisie
 * ou d'un fichier importé qui porte le HT et le TTC), en en déduisant la taxe.
 */
export function buildPriceFromAmounts(
  amountExclTax,
  amountInclTax,
  currency = DEFAULT_CURRENCY,
) {
  const exclTax = toAmount(amountExclTax);
  const inclTax = toAmount(amountInclTax);
  if (exclTax === null && inclTax === null) return null;

  return {
    amountExclTax: exclTax,
    amountInclTax: inclTax,
    // La taxe ne se déduit que si les deux montants sont connus : un prix
    // partiel (HT saisi, TTC laissé vide) reste un prix valide.
    ...(exclTax !== null &&
      inclTax !== null && { amountTax: roundToCents(inclTax - exclTax) }),
    currency,
  };
}

/**
 * Construit un Price à partir du montant hors taxes et d'un taux de taxe
 * exprimé entre 0 et 1 (0.2 pour une TVA à 20 %).
 */
export function buildPriceFromAmountExclTax(
  amountExclTax,
  taxPercentage = 0,
  currency = DEFAULT_CURRENCY,
) {
  const exclTax = toAmount(amountExclTax);
  if (exclTax === null) return null;

  return buildPriceFromAmounts(
    exclTax,
    roundToCents(exclTax * (1 + taxPercentage)),
    currency,
  );
}

/**
 * Construit un Price à partir du montant toutes taxes comprises et d'un taux
 * de taxe exprimé entre 0 et 1 (0.2 pour une TVA à 20 %).
 */
export function buildPriceFromAmountInclTax(
  amountInclTax,
  taxPercentage = 0,
  currency = DEFAULT_CURRENCY,
) {
  const inclTax = toAmount(amountInclTax);
  if (inclTax === null) return null;

  // Le taux porte sur le hors taxes : on retrouve celui-ci en divisant par
  // 1 + taux, pas en retranchant le taux du TTC.
  return buildPriceFromAmounts(
    roundToCents(inclTax / (1 + taxPercentage)),
    inclTax,
    currency,
  );
}

/**
 * Montant hors taxes d'un Price, ou null s'il n'est pas renseigné.
 */
export function getAmountExclTax(price) {
  return toAmount(price?.amountExclTax);
}

/**
 * Montant toutes taxes comprises d'un Price, ou null s'il n'est pas renseigné.
 */
export function getAmountInclTax(price) {
  return toAmount(price?.amountInclTax);
}
